import { AlertCircle, Beer, Loader2, MapPin, Search, Utensils, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { STATUS_CONFIG } from '../constants';
import { useAuth } from '../contexts/AuthContext';
import { captureSilentError } from '../services/posthog';
import { useDataStore } from '../stores/useDataStore';
import { isTransientError } from '../stores/useDataStore';
import { useMapStore } from '../stores/useMapStore';
import { useUIStore } from '../stores/useUIStore';
import { Category, PlaceSearchResult, RecommendationStatus } from '../types';
import { triggerConfetti } from '../utils/confetti';
import { StatusStamp } from './StatusStamp';

interface AddModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
}

const getFriendlyErrorMessage = (error: any): string => {
    if (!error) return 'An unknown error occurred.';

    // Supabase/Postgres Error Codes
    if (error.code === '42501') {
        return 'Permission denied. You may not be allowed to perform this action.';
    }
    if (error.code === '23505') {
        return 'This recommendation already exists.';
    }
    if (isTransientError(error)) {
        return 'Connection hiccup — your shout didn\'t save. Give it another go!';
    }

    return error.message || 'Something went wrong. Please try again.';
};

export const AddModal: React.FC<AddModalProps> = ({ isOpen, onClose, onSubmit }) => {
  const { currentLocation } = useMapStore();
  const { currentUser } = useAuth();
  const { shouts, venues, updateShout } = useDataStore();
  const { showToast } = useUIStore();

  const [activeTab, setActiveTab] = useState<'search' | 'custom'>('search');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<PlaceSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState<PlaceSearchResult | null>(null);
  
  // Modal State for Preview
  const [modalState, setModalState] = useState<'full' | 'collapsed'>('full');
  const [previewPlace, setPreviewPlace] = useState<PlaceSearchResult | null>(null);

  // Form State
  const [status, setStatus] = useState<RecommendationStatus>(RecommendationStatus.RECOMMENDED);
  const [category, setCategory] = useState<Category>(Category.FOOD);
  const [description, setDescription] = useState('');
  const [customName, setCustomName] = useState('');

  // Edit Mode State
  const [isEditing, setIsEditing] = useState(false);
  const [editShoutId, setEditShoutId] = useState<string | null>(null);
  const [duplicateError, setDuplicateError] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<{place?: string, description?: string, customName?: string}>({});

  // Submit guard. The ref is the actual lock (state updates are async, which would
  // leave a window where a second tap gets through); the state only drives the UI.
  const isSubmittingRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Google Services Refs
  const autocompleteService = useRef<any>(null);
  const placesService = useRef<any>(null);
  const placesServiceDiv = useRef<HTMLDivElement>(null);
  const exitedViaPopState = useRef(false);

  // Init Google Services
  useEffect(() => {
    if (isOpen && window.google && window.google.maps && window.google.maps.places) {
      if (!autocompleteService.current) {
        autocompleteService.current = new window.google.maps.places.AutocompleteService();
      }
      if (!placesService.current && placesServiceDiv.current) {
        placesService.current = new window.google.maps.places.PlacesService(placesServiceDiv.current);
      }
    }
  }, [isOpen]);

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setSearchResults([]);
      setSelectedPlace(null);
      setDescription('');
      setCustomName('');
      setStatus(RecommendationStatus.RECOMMENDED);
      setIsEditing(false);
      setEditShoutId(null);
      setDuplicateError(null);
      setFormErrors({});
      setModalState('full');
      setPreviewPlace(null);
      exitedViaPopState.current = false;
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    } else {
      // Cleanup: Remove preview history entry when modal closes
      // But ONLY if we didn't exit via back button already (to avoid double-back)
      useMapStore.getState().clearPreviewPin();
      if (window.history.state?.preview_mode && !exitedViaPopState.current) {
        window.history.back();
      }
    }
  }, [isOpen]);

  // Reset when switching tabs
  useEffect(() => {
    setIsEditing(false);
    setEditShoutId(null);
    setDuplicateError(null);
    setFormErrors({});
    setDescription('');
    setStatus(RecommendationStatus.RECOMMENDED);
    setModalState('full');
    setPreviewPlace(null);
    useMapStore.getState().clearPreviewPin();
    // Clean up history if we were in preview mode
    if (window.history.state?.preview_mode) {
      window.history.back();
    }
  }, [activeTab]);

  // History API Handler for Preview
  useEffect(() => {
      const handlePopState = (event: PopStateEvent) => {
          if (modalState === 'collapsed') {
              // If back button pressed while collapsed, cancel preview
              handleCancelPreview();
          }
      };

      window.addEventListener('popstate', handlePopState);
      return () => window.removeEventListener('popstate', handlePopState);
  }, [modalState]);

  // Search Logic with Google Places
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchTerm.length > 2 && activeTab === 'search' && autocompleteService.current) {
        setIsLoading(true);
        
        const request = {
            input: searchTerm,
            locationBias: { radius: 5000, center: currentLocation } // Bias towards user view
        };

        autocompleteService.current.getPlacePredictions(request, (predictions: any[], status: any) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
                const results = predictions.map(p => ({
                    id: p.place_id,
                    name: p.structured_formatting.main_text,
                    address: p.structured_formatting.secondary_text || '',
                    lat: 0, // Filled later
                    lng: 0
                }));
                setSearchResults(results);
            } else {
                setSearchResults([]);
            }
            setIsLoading(false);
        });

      } else {
        setSearchResults([]);
        setIsLoading(false);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, activeTab, currentLocation]);

  const handleSelectPlace = (place: PlaceSearchResult) => {
    if (placesService.current) {
        setIsLoading(true);
        placesService.current.getDetails({ placeId: place.id }, (placeDetails: any, status: any) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK && placeDetails.geometry) {
                const fullPlace = {
                    ...place,
                    lat: placeDetails.geometry.location.lat(),
                    lng: placeDetails.geometry.location.lng(),
                    name: placeDetails.name || place.name
                };

                // Check for existing shout immediately
                if (currentUser) {
                    const existingVenue = venues.find(v => v.googlePlaceId === place.id);
                    if (existingVenue) {
                        const existingShout = shouts.find(s => s.userId === currentUser.uid && s.venueId === existingVenue.venueId);
                        if (existingShout) {
                            setSelectedPlace(fullPlace);
                            setIsEditing(true);
                            setEditShoutId(existingShout.id);
                            setStatus(existingShout.status);
                            setCategory(existingVenue.category);
                            setDescription(existingShout.description);
                            setFormErrors(p => ({...p, place: undefined}));
                            setSearchResults([]);
                            setSearchTerm(place.name);
                            setIsLoading(false);
                            return; // Skip preview for edit
                        }
                    }
                }

                // New Place -> Enter Preview Mode
                setPreviewPlace(fullPlace);
                setModalState('collapsed');
                useMapStore.getState().showPreviewPin({ lat: fullPlace.lat, lng: fullPlace.lng }, fullPlace.name);
                
                // Pan map
                const mapInstance = useMapStore.getState().mapInstance;
                if(mapInstance) {
                    try {
                        mapInstance.panTo({ lat: fullPlace.lat, lng: fullPlace.lng });
                        mapInstance.setZoom(15);
                    } catch (e) {
                        captureSilentError("AddModal.mapPan", e);
                    }
                }

                // Push history state so back button cancels preview
                window.history.pushState({ ...window.history.state, preview_mode: true }, '');

            } else {
                showToast("Could not fetch details for this place.", 'error');
            }
            setIsLoading(false);
        });
    }
  };

  const handleConfirmPreview = () => {
      if (previewPlace) {
          setSelectedPlace(previewPlace);
          setSearchTerm(previewPlace.name);
          setSearchResults([]);
          setFormErrors(p => ({...p, place: undefined}));
          
          setModalState('full');
          useMapStore.getState().clearPreviewPin();
          
          // Clean history
          if (window.history.state?.preview_mode) {
             window.history.back();
          }
      }
  };

  const handleCancelPreview = () => {
      setPreviewPlace(null);
      setModalState('full');
      useMapStore.getState().clearPreviewPin();
      
      // Clean history if we didn't get here via popstate (manually clicked cancel)
      // If we got here via popstate, the state is already popped.
      // We set the flag so the unmount cleanup doesn't try to go back again
      exitedViaPopState.current = true;
  };

  const handleSubmit = async () => {
    // Validation
    const errors: typeof formErrors = {};
    let hasError = false;

    if (!description) {
        errors.description = "Please add a description!";
        hasError = true;
    }
    if (activeTab === 'search' && !selectedPlace) {
        errors.place = "Please select a place!";
        hasError = true;
    }
    if (activeTab === 'custom' && !customName) {
        errors.customName = "Please name the place!";
        hasError = true;
    }

    if (hasError) {
        setFormErrors(errors);
        return;
    }

    // Guard against double taps creating duplicate venues/shouts.
    // Checked AFTER validation so validation errors always render.
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsSubmitting(true);

    // Confetti Explosion!
    const rect = (document.activeElement as HTMLElement).getBoundingClientRect();
    const x = (rect.left + rect.width / 2) / window.innerWidth;
    const y = (rect.top + rect.height / 2) / window.innerHeight;

    triggerConfetti({ x, y });

    try {
        if (isEditing && editShoutId) {
            await updateShout(currentUser!, editShoutId, status, description, category);
        } else {
            const name = activeTab === 'search' ? selectedPlace!.name : customName;
            const coords = activeTab === 'search' 
                ? { lat: selectedPlace!.lat, lng: selectedPlace!.lng } 
                : { lat: currentLocation.lat, lng: currentLocation.lng };
            
            await onSubmit({
              name,
              ...coords,
              status,
              category,
              description,
              isCustom: activeTab === 'custom',
              googlePlaceId: selectedPlace?.id
            });
        }
        onClose();
    } catch (e: any) {
        captureSilentError("AddModal.submit", e);
        const friendlyMessage = getFriendlyErrorMessage(e);

        // For transient errors, keep the modal open so the user can retry immediately
        if (isTransientError(e)) {
            showToast(friendlyMessage, 'error', {
                action: { label: 'Try Again', onClick: handleSubmit },
                duration: 8000,
            });
        } else if (activeTab === 'search') {
            showToast(friendlyMessage, 'error');
        } else {
            setFormErrors(prev => ({ ...prev, customName: friendlyMessage }));
        }
    } finally {
        // Released here so the toast's 'Try Again' action can re-run handleSubmit.
        isSubmittingRef.current = false;
        setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  // Render Logic
  const isCollapsed = modalState === 'collapsed';

  return (
    <>
        {/* Hidden div for PlacesService - outside backdrop to avoid click interference */}
        <div ref={placesServiceDiv} className="hidden"></div>

        {/* Backdrop - Fade out when collapsed */}
        <div 
            className={`fixed inset-0 z-50 transition-all duration-500 ease-in-out ${isCollapsed ? 'bg-transparent pointer-events-none' : 'bg-brand-dark/60 backdrop-blur-sm'}`}
            onClick={() => !isCollapsed && onClose()}
        >
        {/* Centering container - always uses flexbox, horizontal position never changes */}
        <div className="absolute inset-0 flex items-center justify-center p-4 pointer-events-none">

        {/* Modal - uses translateY to animate vertical position, never changes horizontal centering */}
        <div 
            className={`pointer-events-auto bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col w-full max-w-md transition-all duration-500 ease-spring ${
                isCollapsed 
                    ? 'translate-y-[calc(50vh-50%-1.5rem)] border-2 border-brand-cream-dark' 
                    : 'max-h-[90vh]'
            }`}
            onClick={(e) => e.stopPropagation()}
        >
            
            {/* Header */}
            {!isCollapsed && (
                <div className="p-4 border-b border-brand-cream-dark flex justify-between items-center bg-white z-10">
                <h3 className="font-display font-extrabold text-xl text-brand-dark">Good Shout!</h3>
                <button onClick={onClose} className="p-2 hover:bg-brand-cream-dark rounded-full"><X size={20}/></button>
                </div>
            )}

            {/* Tabs */}
            {!isCollapsed && (
                <div className="flex p-2 gap-2 bg-brand-cream-dark">
                    <button 
                        className={`flex-1 py-2 rounded-xl text-sm font-semibold transition-all ${activeTab === 'search' ? 'bg-white shadow text-brand-red' : 'text-brand-dark-light hover:text-brand-dark'}`}
                        onClick={() => setActiveTab('search')}
                    >
                        Find Place
                    </button>
                    <button 
                        className={`flex-1 py-2 rounded-xl text-sm font-semibold transition-all ${activeTab === 'custom' ? 'bg-white shadow text-brand-red' : 'text-brand-dark-light hover:text-brand-dark'}`}
                        onClick={() => setActiveTab('custom')}
                    >
                        Pin Here
                    </button>
                </div>
            )}

            {/* Scrollable Content */}
            <div className={`overflow-y-auto bg-brand-cream relative transition-all duration-300 ${isCollapsed ? 'p-0 flex-none' : 'p-5 space-y-6 flex-1'}`}>
                
                {/* Context Banners */}
                {!isCollapsed && isEditing && (
                    <div className="mb-2 p-3 bg-brand-steel/10 border border-brand-steel/30 rounded-xl flex items-start gap-3 animate-in fade-in slide-in-from-top-2">
                        <div className="p-1.5 bg-brand-steel/20 rounded-lg text-brand-steel shrink-0">
                            <Loader2 size={16} className="animate-spin" /> 
                        </div>
                        <div>
                            <p className="text-sm font-bold text-brand-dark">Echo... Such a good shout you did it twice! 😳</p>
                            <p className="text-xs text-brand-dark-light mt-0.5">We loaded your existing shout about this place so you can edit it.</p>
                        </div>
                    </div>
                )}

                {!isCollapsed && duplicateError && (
                    <div className="mb-2 p-3 bg-brand-red/10 border border-brand-red/25 rounded-xl flex items-start gap-3 animate-in fade-in slide-in-from-top-2">
                        <div className="p-1.5 bg-brand-red/20 rounded-lg text-brand-red shrink-0">
                            <X size={16} /> 
                        </div>
                        <div>
                            <p className="text-sm font-bold text-brand-red">Already Shouted</p>
                            <p className="text-xs text-brand-red mt-0.5">{duplicateError}</p>
                        </div>
                    </div>
                )}
                
                {/* STEP 1: LOCATION OR PREVIEW BAR */}
                {isCollapsed ? (
                    // PREVIEW BAR UI
                    <div className="p-3 bg-white flex items-center gap-3">
                        <div className="flex-1 min-w-0 bg-brand-cream rounded-xl p-2.5 flex items-center gap-3 border border-status-recommend/50 cursor-pointer hover:bg-brand-cream-dark transition-colors"
                             onClick={() => {
                                 // Cancel on click (search again)
                                 window.history.back(); // Will trigger popstate logic
                             }}
                        >
                             <div className="flex items-center gap-2 min-w-0 flex-1">
                                 <MapPin size={18} className="text-status-recommend-ink shrink-0"/>
                                 <span className="font-bold text-sm text-brand-dark truncate">{previewPlace?.name}</span>
                             </div>
                             <div className="flex items-center gap-1 text-xs text-brand-dark-light shrink-0 pl-2 border-l border-brand-cream-dark">
                                 <span>Change</span>
                                 <X size={14} />
                             </div>
                        </div>

                        <button 
                            onClick={handleConfirmPreview}
                            className="bg-brand-dark text-white px-5 py-3 rounded-xl font-bold text-sm shadow-lg hover:scale-105 active:scale-95 transition-all whitespace-nowrap shrink-0"
                        >
                            Looks good! ✓
                        </button>
                    </div>
                ) : (
                    // NORMAL STEP 1 UI
                    <div className="space-y-3">
                        <label className="text-xs font-bold text-brand-dark-light uppercase tracking-wider">1. Location</label>
                        
                        {activeTab === 'search' ? (
                            <div className="relative">
                                <div className={`flex items-center border-2 rounded-xl overflow-hidden transition-colors bg-white ${selectedPlace ? 'border-status-recommend bg-status-recommend/10' : 'border-brand-cream-dark focus-within:border-brand-red'}`}>
                                    <Search className={`ml-3 ${selectedPlace ? 'text-status-recommend-ink' : 'text-brand-dark-light'}`} size={20} />
                                    <input 
                                        type="text" 
                                        placeholder="Search places..."
                                        className="w-full p-3 outline-none bg-transparent text-brand-dark placeholder:text-brand-dark-light"
                                        value={selectedPlace ? selectedPlace.name : searchTerm}
                                        onChange={(e) => {
                                            setSearchTerm(e.target.value);
                                            setSelectedPlace(null); // Reset selection on type
                                            setFormErrors(p => ({...p, place: undefined}));
                                        }}
                                    />
                                    {isLoading && <Loader2 className="mr-3 animate-spin text-brand-red" size={20} />}
                                    {selectedPlace && (
                                        <button onClick={() => {setSelectedPlace(null); setSearchTerm('')}} className="mr-3 text-brand-dark-light hover:text-brand-dark"><X size={16}/></button>
                                    )}
                                </div>
                                {formErrors.place && (
                                    <p className="mt-2 text-xs text-brand-red font-bold flex items-center gap-1 animate-in slide-in-from-top-1">
                                        <AlertCircle size={12} /> {formErrors.place}
                                    </p>
                                )}
                                
                                {/* Search Results Dropdown */}
                                {!selectedPlace && searchResults.length > 0 && (
                                    <div className={`absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-brand-cream-dark z-20 max-h-40 overflow-y-auto ${isLoading ? 'pointer-events-none opacity-50' : ''}`}>
                                        {searchResults.map(res => (
                                            <div 
                                                key={res.id} 
                                                className="p-3 hover:bg-brand-cream cursor-pointer border-b border-brand-cream-dark last:border-0"
                                                onClick={() => handleSelectPlace(res)}
                                            >
                                                <p className="font-bold text-brand-dark text-sm">{res.name}</p>
                                                <p className="text-xs text-brand-dark-light truncate">{res.address}</p>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="space-y-2">
                                <div className="p-3 bg-brand-red/10 rounded-xl border border-brand-red/20 text-brand-red text-sm flex items-center gap-2">
                                    <MapPin size={16} />
                                    Pinning at center of current map view
                                </div>
                                <input 
                                    type="text" 
                                    placeholder="What's this place called?" 
                                    className="w-full p-3 border-2 border-brand-cream-dark rounded-xl outline-none focus:border-brand-red bg-white"
                                    value={customName}
                                    onChange={(e) => {
                                        setCustomName(e.target.value);
                                        setFormErrors(p => ({...p, customName: undefined}));
                                    }}
                                />
                                {formErrors.customName && (
                                    <p className="mt-2 text-xs text-brand-red font-bold flex items-center gap-1 animate-in slide-in-from-top-1">
                                        <AlertCircle size={12} /> {formErrors.customName}
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                )}
                

                {/* STEP 2: CATEGORY */}
                {!isCollapsed && (
                    <div className="space-y-3">
                        <label className="text-xs font-bold text-brand-dark-light uppercase tracking-wider">2. Category</label>
                        <div className="flex gap-3">
                            <button 
                                onClick={() => setCategory(Category.FOOD)}
                                className={`flex-1 py-3 rounded-xl border-2 flex flex-col items-center gap-1 transition-all bg-white ${category === Category.FOOD ? 'border-cat-food-fg bg-cat-food-bg text-cat-food-fg' : 'border-brand-cream-dark text-brand-dark-light hover:bg-brand-cream-dark'}`}
                            >
                                <Utensils size={24} />
                                <span className="text-xs font-bold">Food</span>
                            </button>
                            <button 
                                onClick={() => setCategory(Category.DRINKS)}
                                className={`flex-1 py-3 rounded-xl border-2 flex flex-col items-center gap-1 transition-all bg-white ${category === Category.DRINKS ? 'border-cat-drinks-fg bg-cat-drinks-bg text-cat-drinks-fg' : 'border-brand-cream-dark text-brand-dark-light hover:bg-brand-cream-dark'}`}
                            >
                                <Beer size={24} />
                                <span className="text-xs font-bold">Drinks</span>
                            </button>
                        </div>
                    </div>
                )}

                {/* STEP 3: STATUS */}
                {!isCollapsed && (
                    <div className="space-y-3">
                        <label className="text-xs font-bold text-brand-dark-light uppercase tracking-wider">3. Verdict</label>
                        <div className="grid grid-cols-2 gap-2">
                            {[
                                RecommendationStatus.SEAL_OF_APPROVAL,
                                RecommendationStatus.RECOMMENDED,
                                RecommendationStatus.ITS_ALRIGHT,
                                RecommendationStatus.WANT_TO_VISIT,
                                RecommendationStatus.DO_NOT_RECOMMEND
                            ].map((s, i, arr) => {
                                const config = STATUS_CONFIG[s];
                                const isSelected = status === s;
                                // Odd count: let the last tile span the full row so the grid stays even.
                                const isLastOdd = i === arr.length - 1 && arr.length % 2 === 1;
                                return (
                                    <button
                                        key={s}
                                        onClick={() => setStatus(s)}
                                        className={`rounded-lg transition-transform active:scale-95 ${isLastOdd ? 'col-span-2' : ''}`}
                                    >
                                        <StatusStamp
                                            status={s}
                                            size="md"
                                            rotate={false}
                                            filled={isSelected}
                                            animate={isSelected}
                                            label={config.label}
                                            labelMaxWidth="160px"
                                            className="w-full justify-center min-h-[46px]"
                                        />
                                    </button>
                                )
                            })}
                        </div>
                    </div>
                )}

                {/* STEP 4: DESCRIPTION */}
                {!isCollapsed && (
                    <div className="space-y-3">
                        <label className="text-xs font-bold text-brand-dark-light uppercase tracking-wider">4. Why though?</label>
                        <textarea 
                            className="w-full p-3 border-2 border-brand-cream-dark rounded-xl outline-none focus:border-brand-red min-h-[80px] text-sm resize-none bg-white"
                            placeholder="Essential dish? Best time to go? Honest thoughts..."
                            value={description}
                            onChange={(e) => {
                                setDescription(e.target.value);
                                setFormErrors(p => ({...p, description: undefined}));
                            }}
                        ></textarea>
                        {formErrors.description && (
                            <p className="mt-2 text-xs text-brand-red font-bold flex items-center gap-1 animate-in slide-in-from-top-1">
                                <AlertCircle size={12} /> {formErrors.description}
                            </p>
                        )}
                    </div>
                )}
            </div>

            {/* Footer */}
            {!isCollapsed && (
                <div className="p-4 border-t border-brand-cream-dark bg-white">
                    <button
                        onClick={handleSubmit}
                        // Only disabled while a submit is in flight — validation errors are
                        // still surfaced by clicking, so the button stays enabled when idle.
                        disabled={isSubmitting}
                        className={`w-full py-3 text-white rounded-xl font-bold shadow-lg transition active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed disabled:active:scale-100 ${isEditing ? 'bg-brand-steel hover:bg-brand-steel/85' : 'bg-brand-red hover:bg-brand-red-hover'}`}
                    >
                        {isSubmitting
                            ? (isEditing ? 'Saving...' : 'Shouting... 📣')
                            : (isEditing ? 'Save Changes' : 'Shout it! 📣')}
                    </button>
                </div>
            )}

        </div>
        </div>
        </div>
    </>
  );
};